import { LanCode } from "@/store/reducers/setting";
import { PageSeoData, SeoCategory, SeoData, SeoPageConfig } from "@/types/seo";
import { staticSeoData } from "./static-seo-data";

/**
 * SEO 페이지 설정 레지스트리
 * - sitemap 생성과 SEO 메타데이터를 통합 관리
 */
export const seoPageConfigs: SeoPageConfig[] = [
  // === 정적 페이지 ===
  {
    path: "/",
    category: "Home",
    priority: 1.0,
    changefreq: "weekly",
    seo: staticSeoData.Home ?? null,
    isDynamic: false,
  },
  {
    path: "/tactsuit",
    category: "Shop",
    priority: 0.9,
    changefreq: "weekly",
    seo: staticSeoData.Shop ?? null,
    isDynamic: false,
  },
  {
    path: "/games",
    category: "Games",
    priority: 0.9,
    changefreq: "daily",
    seo: staticSeoData.Games ?? null,
    isDynamic: false,
  },
  // `/games` 의 접두 매칭보다 뒤에 둔다 — 정확 매칭은 순서와 무관하게 먼저 잡히고, 하위 경로(`/games/pc/games` 등)는
  // 등록 안 된 하위 경로만 「Games」를 받는다(목록 · 소프트웨어 4페이지는 아래에 따로 등록).
  {
    path: "/games/pc",
    category: "GamesPc",
    priority: 0.9,
    changefreq: "daily",
    seo: staticSeoData.GamesPc ?? null,
    isDynamic: false,
  },
  {
    path: "/games/vr",
    category: "GamesVr",
    priority: 0.9,
    changefreq: "daily",
    seo: staticSeoData.GamesVr ?? null,
    isDynamic: false,
  },
  {
    path: "/games/pc/games",
    category: "GamesPcList",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.GamesPcList ?? null,
    isDynamic: false,
  },
  {
    path: "/games/vr/games",
    category: "GamesVrList",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.GamesVrList ?? null,
    isDynamic: false,
  },
  {
    path: "/games/pc/software",
    category: "GamesPcSoftware",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.GamesPcSoftware ?? null,
    isDynamic: false,
  },
  {
    path: "/games/vr/software",
    category: "GamesVrSoftware",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.GamesVrSoftware ?? null,
    isDynamic: false,
  },
  {
    path: "/compatibility",
    category: "Compatibility",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.Compatibility ?? null,
    isDynamic: false,
  },
  {
    path: "/software",
    category: "Software",
    priority: 0.8,
    changefreq: "weekly",
    seo: staticSeoData.Software ?? null,
    isDynamic: false,
  },
  {
    path: "/support",
    category: "Support",
    priority: 0.7,
    changefreq: "weekly",
    seo: staticSeoData.Support ?? null,
    isDynamic: false,
  },
  {
    path: "/news",
    category: "News",
    priority: 0.8,
    changefreq: "daily",
    seo: staticSeoData.News ?? null,
    isDynamic: false,
  },

  // === 동적 페이지 ===
  {
    path: "/games/experiences/:slug",
    category: "GameDetail",
    priority: 0.8,
    changefreq: "weekly",
    seo: null,
    isDynamic: true,
  },
  {
    path: "/shop/buy-tactsuit/:slug",
    category: "Product",
    priority: 0.9,
    changefreq: "daily",
    seo: null,
    isDynamic: true,
  },
  {
    path: "/news/:id",
    category: "NewsDetail",
    priority: 0.7,
    changefreq: "monthly",
    seo: null,
    isDynamic: true,
  },
  {
    path: "/blog/:id",
    category: "BlogDetail",
    priority: 0.7,
    changefreq: "monthly",
    seo: null,
    isDynamic: true,
  },
];

/**
 * 동적 라우트 패턴 정규식 사전 계산 (모듈 초기화 시 1회)
 * - findSeoConfig 호출마다 new RegExp를 재생성하지 않도록 hoist
 */
const dynamicSeoConfigRegexes = seoPageConfigs
  .filter((c) => c.isDynamic)
  .map((c) => ({
    config: c,
    regex: new RegExp(`^${c.path.replace(/:\w+/g, "[^/]+")}$`),
  }));

/**
 * 경로에서 SEO 설정 찾기
 */
export function findSeoConfig(pathname: string): SeoPageConfig | null {
  // 1. 정확한 매칭 우선
  const exactMatch = seoPageConfigs.find(
    (c) => !c.isDynamic && c.path === pathname,
  );
  if (exactMatch) return exactMatch;

  // 2. 동적 라우트 패턴 매칭 (사전 계산된 정규식 사용)
  for (const { config, regex } of dynamicSeoConfigRegexes) {
    if (regex.test(pathname)) {
      return config;
    }
  }

  // 3. Prefix 매칭 (fallback)
  const prefixMatch = seoPageConfigs.find(
    (c) => !c.isDynamic && pathname.startsWith(c.path) && c.path !== "/",
  );
  if (prefixMatch) return prefixMatch;

  // 4. 기본값: General
  return seoPageConfigs[0];
}

/**
 * 경로와 언어에 맞는 SEO 데이터 가져오기
 */
export function getSeoData(pathname: string, locale: LanCode): SeoData {
  const config = findSeoConfig(pathname);

  if (config?.seo) {
    const localizedSeo = config.seo[locale];
    if (localizedSeo) return localizedSeo;
  }

  // Fallback: General English
  return (
    staticSeoData.General?.[LanCode.English] ?? {
      title: "bHaptics",
      description: "Next generation haptic technology",
    }
  );
}

/**
 * 페이지가 넘긴 SEO 와 경로의 언어별 정적 SEO 를 합친다 — **넘긴 칸만 덮고, 빈 칸은 정적 값으로 채운다.**
 *
 * ⚠️ 종전 `Header` 는 `seo ?? 정적` 이라 JSON-LD 만 넘겨도 정적 데이터를 통째로 버렸다 — `/tactsuit` 가
 * FAQ JSON-LD 만 넘기면서 title · description · og 가 전부 빈 값이 됐고(2026-03-20 `645880c12` ~), 홈은
 * 영어 title · description 을 직접 넘겨 7개 언어가 전부 영어였다(2026-02-26 `6ed04b9b0` ~).
 */
export function resolvePageSeo(
  seo: Partial<PageSeoData> | null | undefined,
  fallback: SeoData,
): SeoData & Partial<PageSeoData> {
  return {
    ...fallback,
    ...seo,
    title: seo?.title || fallback.title,
    description: seo?.description || fallback.description,
  };
}

/**
 * 카테고리명으로 SEO 데이터 가져오기
 */
export function getSeoDataByCategory(
  category: SeoCategory,
  locale: LanCode,
): SeoData | null {
  const data = staticSeoData[category];
  if (!data) return null;
  return data[locale] ?? data[LanCode.English] ?? null;
}
